// FlixPlayerActionButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import NeedAuth from '../container/NeedAuth.js';

import numberWithCommas from '../resource/numberWithCommas.js';
import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { QUICKPAY_BUNDLE_SECTION } from '../resource/shopConstants.js';

import media from '../style/media.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import ButtonShimmerStyle from '../style/ButtonShimmerStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { textColor, color, loadingIconOpacity } from '../style/variables.js';

import DiamondIconResource from '../../img/point_2.svg';
import LoadingImageResource from '../../img/img_loading_white_20.svg';

export class FlixPlayerActionButton extends React.PureComponent {
  state = { price: null };

  handleClickLogin = () => {
    const { login } = this.props;
    return login();
  };

  handleClickUnlock = async () => {
    const {
      canViewMedia,
      messageId,
      drmFeatureName,
      sendUnlockMessage,
      replace,
      isAuthed,
      isBalanceInsufficient,
      hasPurchased,
      openQuickpayModal,
    } = this.props;
    if (canViewMedia) {
      return replace(`/post/${messageId}`);
    }
    if (!isAuthed || (!hasPurchased && isBalanceInsufficient)) {
      openQuickpayModal({
        sectionId: QUICKPAY_BUNDLE_SECTION.VIDEO,
      });
      return;
    }
    sendUnlockMessage({
      id: messageId,
      shouldCheckDrm: true,
      drmFeatureName,
    });
  };
  renderLoading = () => {
    return (
      <LoadingWrapper>
        <LoadingIcon src={LoadingImageResource} alt="Loading" />
      </LoadingWrapper>
    );
  };
  renderUnlockButtonContent = () => {
    const {
      isAuthed,
      isBalanceInsufficient,
      canViewMedia,
      hasPurchased,
      t,
      unlockPrice,
    } = this.props;
    if (!isAuthed || (!hasPurchased && isBalanceInsufficient)) {
      return (
        <ActionButtonText>
          {t('cta_unlock_video_zero_purchase', {
            ns: TranslationNamespace.POST,
          })}
        </ActionButtonText>
      );
    }
    return (
      <>
        <ActionButtonText>
          {t('button_watch_full_video', {
            ns: TranslationNamespace.POST,
          })}
        </ActionButtonText>
        {!canViewMedia && (
          <>
            <Divider />
            <DiamondIcon src={DiamondIconResource} alt="diamond" />
            {unlockPrice
              ? numberWithCommas({ number: unlockPrice })
              : unlockPrice}
          </>
        )}
      </>
    );
  };
  render() {
    const {
      badges,
      canViewMedia,
      isAuthed,
      hasPurchased,
      messageId,
      unlockButtonStatus,
      isBalanceInsufficient,
    } = this.props;
    const { price } = this.state;
    let unlockPrice = this.props.unlockPrice;
    if (price) {
      unlockPrice = price;
    }
    const shouldShowLoading = 'loading' === unlockButtonStatus;
    const shouldShowShimmer = !hasPurchased && isBalanceInsufficient;

    const abData = { unlockPrice };
    return (
      <StyledFlixPlayerActionButton>
        <NeedAuth>
          <ActionButton
            data-element_id={
              canViewMedia
                ? ButtonId.Detail.ButtonFreeDetailSignup
                : ButtonId.Detail.ButtonDetailSignup
            }
            data-tracking_payload={{
              messageId,
              abData,
              badges: badges?.join(','),
            }}
            data-key="needAuth"
            isFilled={isAuthed && hasPurchased}
            shouldShowShimmer={shouldShowShimmer}
            onClick={() => {
              this.handleClickUnlock();
            }}
          >
            {this.renderUnlockButtonContent()}
          </ActionButton>
          <ActionButton data-key="authing" disabled isFilled>
            {this.renderLoading()}
          </ActionButton>
          <ActionButton
            data-element_id={ButtonId.All.ButtonUnlock}
            data-tracking_payload={{
              messageId,
              abData,
              badges: badges?.join(','),
            }}
            data-key="authed"
            disabled={shouldShowLoading}
            shouldShowShimmer={shouldShowShimmer}
            onClick={this.handleClickUnlock}
          >
            {shouldShowLoading
              ? this.renderLoading()
              : this.renderUnlockButtonContent()}
          </ActionButton>
        </NeedAuth>
      </StyledFlixPlayerActionButton>
    );
  }
}

FlixPlayerActionButton.propTypes = {
  messageId: PropTypes.string,
  unlockButtonStatus: PropTypes.string,
  drmFeatureName: PropTypes.string,
  unlockPrice: PropTypes.number,
  canViewMedia: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isBalanceInsufficient: PropTypes.bool,
  badges: PropTypes.array,
  hasPurchased: PropTypes.bool,
  login: PropTypes.func,
  sendUnlockMessage: PropTypes.func,
  replace: PropTypes.func,
  openQuickpayModal: PropTypes.func,
  t: PropTypes.func.isRequired,
};

FlixPlayerActionButton.defaultProps = {
  messageId: null,
  unlockButtonStatus: null,
  drmFeatureName: null,
  unlockPrice: null,
  canViewMedia: false,
  isAuthed: false,
  isBalanceInsufficient: false,
  badges: [],
  hasPurchased: false,
  login: () => null,
  sendUnlockMessage: () => null,
  replace: () => null,
  openQuickpayModal: () => null,
};

const StyledFlixPlayerActionButton = styled.div`
  width: 100%;
  max-width: 1200px;
`;

const ActionButton = styled(({ isFilled, shouldShowShimmer, ...restProps }) => (
  <button {...restProps} />
)).attrs(({ isFilled }) => ({
  style: {
    backgroundColor: isFilled ? color.tealBlue : 'rgba(25, 25, 25, 0.8)',
    color: isFilled ? color.neutral[0] : textColor.primary,
  },
}))`
  ${ResetButtonStyle};
  ${({ shouldShowShimmer }) => shouldShowShimmer && ButtonShimmerStyle};
  --button-shine-color: ${color.tealBlue_alpha_10};
  border-color: ${color.tealBlue};
  border-style: solid;
  border-width: 1px;
  border-radius: 4px;
  padding-right: 16px;
  padding-left: 16px;
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  ${media.mobile`
    height: 36px;
  `}
`;

const ActionButtonText = styled.div`
  ${TextEllipsis};
`;

const Divider = styled.div`
  margin-left: 8px;
  margin-right: 8px;
  width: 1px;
  height: 28px;
  background-color: ${color.tealBlue};
  ${media.mobile`
    height: 16px;
  `};
`;

const DiamondIcon = styled.img`
  margin-right: 4px;
  width: auto;
  height: 32px;
  ${media.mobile`
    height: 16px;
  `}
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${loadingIconOpacity};
`;

const LoadingIcon = styled.img`
  width: auto;
  height: 32px;
  ${media.mobile`
    height: 12px;
  `}
`;

export default withTranslation()(FlixPlayerActionButton);
